<c-container [fluid]="true" class="legal-audit">

  <h1 class="h4 mb-1">Legal Acceptances</h1>
  <p class="text-body-secondary mb-4">
    Immutable record of every legal document acceptance. Records cannot be edited or deleted.
  </p>

  @if (stats(); as s) {
    @if (s.misconfigured.length) {
      <!-- A configured document with no template is not asked for at all, so consent
           for it silently stops being collected. Surfaced loudly rather than left in
           the logs. -->
      <c-alert color="danger" class="mb-4">
        <strong>Documents configured but not being asked for</strong>
        <p class="mb-2">
          These are listed in legal_config.json but have no policy template on disk, so
          customers are never shown them and no consent is being recorded.
        </p>
        <ul class="mb-0">
          @for (m of s.misconfigured; track m.config_domain_id + m.document_key) {
            <li>config domain {{ m.config_domain_id }} — <code>{{ m.document_key }}</code>: {{ m.reason }}</li>
          }
        </ul>
      </c-alert>
    }

    <c-row class="mb-4">
      <c-col [md]="3" class="mb-3">
        <c-card><c-card-body>
          <div class="text-body-secondary small">Total acceptances</div>
          <div class="fs-4 fw-semibold">{{ s.total | number }}</div>
        </c-card-body></c-card>
      </c-col>
      <c-col [md]="3" class="mb-3">
        <c-card><c-card-body>
          <div class="text-body-secondary small">Last 7 days</div>
          <div class="fs-4 fw-semibold">{{ s.last_7_days | number }}</div>
        </c-card-body></c-card>
      </c-col>
      <c-col [md]="6" class="mb-3">
        <c-card><c-card-body>
          <div class="text-body-secondary small mb-2">Published documents</div>
          <div class="legal-audit__published">
            @for (doc of s.published; track doc.config_domain_id + doc.document_key) {
              <span class="legal-audit__pub-item">
                <c-badge [color]="doc.enabled ? 'success' : 'secondary'">
                  d{{ doc.config_domain_id }}
                </c-badge>
                {{ doc.document_key }} v{{ doc.current_version }}
                <span class="text-body-secondary">({{ doc.accepted_count | number }})</span>
              </span>
            } @empty {
              <span class="text-body-secondary">No documents configured.</span>
            }
          </div>
        </c-card-body></c-card>
      </c-col>
    </c-row>
  }

  <c-card class="mb-4">
    <c-card-header>Filters</c-card-header>
    <c-card-body>
      <c-row class="g-2 align-items-end">
        <c-col [md]="2">
          <label class="form-label small">User ID</label>
          <input cFormControl type="number" [(ngModel)]="filters.user_id" placeholder="Any">
        </c-col>
        <c-col [md]="2">
          <label class="form-label small">Document</label>
          <input cFormControl type="text" [(ngModel)]="filters.document_key" placeholder="e.g. terms">
        </c-col>
        <c-col [md]="2">
          <label class="form-label small">Version</label>
          <input cFormControl type="text" [(ngModel)]="filters.document_version" placeholder="e.g. 1.0">
        </c-col>
        <c-col [md]="2">
          <label class="form-label small">From</label>
          <input cFormControl type="date" [(ngModel)]="filters.start_date">
        </c-col>
        <c-col [md]="2">
          <label class="form-label small">To</label>
          <input cFormControl type="date" [(ngModel)]="filters.end_date">
        </c-col>
        <c-col [md]="2" class="d-flex gap-2">
          <button cButton color="primary" (click)="applyFilters()">Apply</button>
          <button cButton color="secondary" variant="outline" (click)="resetFilters()">Reset</button>
        </c-col>
      </c-row>
    </c-card-body>
  </c-card>

  <c-card>
    <c-card-header>
      Acceptances
      <span class="text-body-secondary small">({{ pagination().total | number }})</span>
    </c-card-header>
    <c-card-body>
      @if (loading()) {
        <div class="text-center py-5"><c-spinner color="primary"></c-spinner></div>
      } @else if (loadError()) {
        <div class="text-center py-5">
          <p class="text-danger mb-3">Could not load acceptances.</p>
          <button cButton color="primary" (click)="load()">Try again</button>
        </div>
      } @else if (!records().length) {
        <p class="text-body-secondary text-center py-5 mb-0">No acceptances match these filters.</p>
      } @else {
        <div class="table-responsive">
          <table cTable hover class="align-middle">
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>User</th>
                <th>Document</th>
                <th>Version</th>
                <th>Site</th>
                <th>Country</th>
                <th>Device</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (r of records(); track r.id) {
                <tr>
                  <td class="text-nowrap">{{ r.accepted_at | date: 'yyyy-MM-dd HH:mm:ss' : 'UTC' }}</td>
                  <td>{{ r.user_label }}</td>
                  <td>{{ r.document_key }}</td>
                  <td><c-badge color="info">{{ r.document_version }}</c-badge></td>
                  <td>d{{ r.config_domain_id }}</td>
                  <td>{{ r.country || '—' }}</td>
                  <td class="small text-body-secondary">
                    {{ r.browser || '—' }}<br>{{ r.platform }} {{ r.device_type ? '· ' + r.device_type : '' }}
                  </td>
                  <td class="text-end">
                    <button cButton color="secondary" variant="ghost" size="sm" (click)="openDetail(r)">
                      Details
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (pagination().total_pages > 1) {
          <c-pagination class="mt-3 mb-0" aria-label="Acceptance pages">
            <li cPageItem [disabled]="pagination().page === 1">
              <a cPageLink (click)="goToPage(pagination().page - 1)">Previous</a>
            </li>
            @for (p of pages(); track p) {
              <li cPageItem [active]="p === pagination().page">
                <a cPageLink (click)="goToPage(p)">{{ p }}</a>
              </li>
            }
            <li cPageItem [disabled]="pagination().page === pagination().total_pages">
              <a cPageLink (click)="goToPage(pagination().page + 1)">Next</a>
            </li>
          </c-pagination>
        }
      }
    </c-card-body>
  </c-card>

  <c-modal size="lg" [visible]="detailOpen()" (visibleChange)="onDetailVisibleChange($event)" [scrollable]="true">
    <c-modal-header>
      <h5 cModalTitle>Acceptance detail</h5>
    </c-modal-header>
    <c-modal-body>
      @if (detail(); as d) {
        <table cTable class="align-middle mb-0">
          <tbody>
            <tr><td class="fw-semibold">Document</td><td>{{ d.document_key }} <c-badge color="info">v{{ d.document_version }}</c-badge></td></tr>
            <tr><td class="fw-semibold">Language shown</td><td>{{ d.document_locale }}</td></tr>
            <tr><td class="fw-semibold">User</td><td>{{ d.user_label }}</td></tr>
            <tr><td class="fw-semibold">Accepted at (UTC)</td><td>{{ d.accepted_at | date: 'yyyy-MM-dd HH:mm:ss' : 'UTC' }}</td></tr>
            <tr><td class="fw-semibold">Flow</td><td>{{ d.flow }}</td></tr>
            <tr>
              <td class="fw-semibold">Submission</td>
              <td><code class="small">{{ d.bundle_id }}</code>
                <div class="small text-body-secondary">Documents sharing this id were confirmed together.</div>
              </td>
            </tr>
            <tr><td class="fw-semibold">Site / tenant</td><td>config domain {{ d.config_domain_id }} · user domain {{ d.domain_id }}</td></tr>
            <tr><td class="fw-semibold">IP address</td><td>{{ d.ip_address || '—' }}</td></tr>
            <tr>
              <td class="fw-semibold">Country</td>
              <td>
                {{ d.country || '—' }}
                @if (d.geoip_source) {
                  <div class="small text-body-secondary">
                    <!-- No space before the comma: @if preserves the whitespace inside its block. -->
                    Resolved by {{ d.geoip_source }}@if (d.geoip_build) {<span>, dataset built {{ d.geoip_build | date: 'yyyy-MM-dd' : 'UTC' }}</span>}
                  </div>
                }
              </td>
            </tr>
            <tr><td class="fw-semibold">Browser</td><td>{{ d.browser || '—' }}</td></tr>
            <tr><td class="fw-semibold">Platform / device</td><td>{{ d.platform || '—' }} {{ d.device_type ? '· ' + d.device_type : '' }}</td></tr>
            <tr><td class="fw-semibold">UI language</td><td>{{ d.language || '—' }}</td></tr>
            @if (d.user_agent) {
              <tr><td class="fw-semibold">User agent</td><td><code class="small">{{ d.user_agent }}</code></td></tr>
            }
          </tbody>
        </table>
      } @else {
        <div class="text-center py-4"><c-spinner color="primary"></c-spinner></div>
      }
    </c-modal-body>
    <c-modal-footer>
      <button cButton color="secondary" (click)="closeDetail()">Close</button>
    </c-modal-footer>
  </c-modal>

</c-container>
